import { Spacing } from '@persona-id/design-tokens';
import { ClockIcon, CopyIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { createRef, ReactNode } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import AttributeItem from 'dashboard/components/AttributeItem';
import CaseStatusSticker from 'dashboard/components/CaseStatusSticker';
import { InfoGrid } from 'dashboard/layouts/ShowViewLayoutV2';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { Case } from 'dashboard/models/case';
import { ProductFeatureName } from 'dashboard/models/organization';
import { User } from 'dashboard/models/user';
import { CaseInfoAttribute, ModuleCaseInfo } from 'dashboard/modules/CaseInfo/model';
import { formatSlaTime } from 'dashboard/views/DashboardCasesView/ConfigurableCasesTable/CasesTable';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Popover from 'design-system/components/Popover';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { fadeIn } from 'design-system/variables/keyframes';
import { TransitionDuration } from 'design-system/variables/transitions';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import useProductFeature from 'lib/hooks/useProductFeature';

const TitleContainer = styled('div')`
  display: grid;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: auto auto;
  align-items: center;
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
`;

export interface CaseInfoProps {
  module: ModuleCaseInfo;
  case: Case;
  assignee: User | undefined;
  accountIds?: string[];
  referenceIds?: string[];
}

const CaseInfo = ({
  module,
  referenceIds = [],
  accountIds = [],
  assignee,
  case: kase,
}: CaseInfoProps) => {
  const caseTitle = kase.attributes.name;
  const assigneeDisplay = userDisplayName(assignee);
  const referenceId = referenceIds.join(', ');
  const attributeList = module.attributes;
  const hasCasesEnhanced = useProductFeature(ProductFeatureName.CasesEnhanced);
  const hasCasesEnterprise = useProductFeature(ProductFeatureName.CasesEnterprise);

  const assigneeAttribute = (
    <AttributeItem
      title='Assignee'
      key='Assignee'
      value={assigneeDisplay}
      isRedacted={false}
      placeholder='Unassigned'
      wordBreak='break-all'
      dataTest='case-details-info__assignee-name'
    />
  );

  const referenceIdAttribute = (
    <AttributeItem
      title='Reference ID'
      key='Reference ID'
      value={referenceId && <CopyableField copyValue={referenceId} value={<p>{referenceId}</p>} />}
      isRedacted={false}
      placeholder='No reference ID'
      wordBreak='break-all'
      width='100%'
      dataTest='case-details-info__reference-id'
    />
  );

  const accountsAttributes = accountIds.map((accountId) => {
    return (
      <AttributeItem
        title='Account'
        key={accountId}
        width='100%'
        value={
          accountId && (
            <CopyableField
              copyValue={accountId}
              value={
                <StyledLink
                  to={`/dashboard/accounts/${accountId}`}
                  data-test='case-details-info__account-link'
                >
                  {accountId}
                </StyledLink>
              }
            />
          )
        }
        isRedacted={false}
        placeholder='No account ID'
        wordBreak='break-all'
      />
    );
  });
  const timeToSlaExpiration = kase.attributes['sla-expires-at']
    ? DateTime.fromISO(kase.attributes['sla-expires-at']).diff(DateTime.local(), 'seconds').seconds
    : null;
  const slaTimeFormatted = formatSlaTime(kase.attributes['sla-expires-at']);
  const slaValue = kase.attributes['sla-expires-at'] && (
    <span>
      {DateTime.fromISO(kase.attributes['sla-expires-at']).toFormat(DATE_AND_TIME_FORMAT)}&nbsp;
      {!kase.attributes.state['end-state'] && (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <Sticker icon={<ClockIcon />} variant={timeToSlaExpiration < 0 ? 'red' : 'basic'}>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          {timeToSlaExpiration < 0
            ? `${slaTimeFormatted} overdue`
            : `${slaTimeFormatted} remaining`}
        </Sticker>
      )}
    </span>
  );

  const slaAttribute = (hasCasesEnhanced || hasCasesEnterprise) && (
    <AttributeItem
      title='Deadline'
      key='SLA'
      value={slaValue}
      isRedacted={false}
      placeholder='None'
      wordBreak='break-all'
      dataTest='case-details-info__reference-id'
    />
  );

  const createdAtAttribute = (
    <AttributeItem
      title='Created at'
      key='Created at'
      value={DateTime.fromISO(kase.attributes['created-at']).toFormat(DATE_AND_TIME_FORMAT)}
      isRedacted={false}
      wordBreak='break-all'
      dataTest='case-details-info__created-at'
    />
  );

  const updatedAtAttribute = (
    <AttributeItem
      title='Updated at'
      key='Updated at'
      value={DateTime.fromISO(kase.attributes['updated-at']).toFormat(DATE_AND_TIME_FORMAT)}
      isRedacted={false}
      wordBreak='break-all'
      dataTest='case-details-info__updated-at'
    />
  );

  const getAttributeByType = (attr: CaseInfoAttribute) => {
    switch (attr) {
      case 'assignee':
        return assigneeAttribute;
      case 'reference-ids':
        return referenceIdAttribute;
      case 'accounts':
        return accountsAttributes;
      case 'sla':
        return slaAttribute;
      case 'created-at':
        return createdAtAttribute;
      case 'updated-at':
        return updatedAtAttribute;
    }
  };

  return (
    <Card data-test={`module__case-info--${module.token}`}>
      <Card.Body>
        <Card.Title>
          <TitleContainer>
            {caseTitle}
            <CaseStatusSticker caseState={kase.attributes.state} />
          </TitleContainer>
        </Card.Title>

        <InfoGrid>{attributeList.map((attribute) => getAttributeByType(attribute))}</InfoGrid>
      </Card.Body>
    </Card>
  );
};

const CopyableFieldContainer = styled.div`
  display: flex;
  gap: ${Spacing[3]};
  align-items: center;
  justify-content: space-between;
`;

const StyledCard = styled(Card)`
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} ${TransitionDuration.Fast} ease-in-out;
  margin: 0;
  padding: ${Spacing[2]};
`;

const HiddenInput = styled.input`
  opacity: 0;
  position: absolute;
  pointer-events: none;
`;

interface CopyableAttributeProps {
  copyValue: string;
  value: ReactNode;
}

const CopyableField = ({ copyValue, value }: CopyableAttributeProps) => {
  const valueInput = createRef<HTMLInputElement>();

  const copyField = () => {
    // Copies from the hidden input so we don't have to display the field itself as
    // an input.
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    valueInput.current.select();
    document.execCommand('copy');
  };

  return (
    <div>
      <HiddenInput value={copyValue} ref={valueInput} readOnly tabIndex={-1} />
      <CopyableFieldContainer>
        {value}
        <Popover
          trigger={
            <Button
              id='copyable-field__copy-button'
              type='button'
              variant='inverse'
              width='icon'
              onClick={copyField}
            >
              <CopyIcon />
            </Button>
          }
        >
          <Popover.Content side='top' align='center'>
            <StyledCard elevated>
              <Paragraph>Copied!</Paragraph>
            </StyledCard>
          </Popover.Content>
        </Popover>
      </CopyableFieldContainer>
    </div>
  );
};

export default CaseInfo;
